import { Box, Grid, LinearProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import {
  useGetSalesOpportunities,
  useSalesOpportunityConversion,
} from '../../../services/modules/orderbook/orderbook.queries';
import { OrderBookOrder } from '../../../stores/OrderBook/orderBook.types';
import {
  SalesOpportunity,
  SalesOpportunityConversion,
} from '../../sales-opportunity/salesOpportunity.types';
import { OrderbookIds } from '../enums/orderbookElementsIds';
import { useOrderBookStyles } from '../styles/OrderBookStyles';
import SalesOpportunityCard from './SalesOpportunityCard';
import SalesOpportunityDetails from './SalesOpportunityDetails';

interface ICustomerSalesOpportunitiesProps {
  order: OrderBookOrder;
}

interface SalesOpportunityGridItemProps {
  haveMoreThanOneResult: boolean;
  testId: string;
  isSelected: boolean;
  opportunity: SalesOpportunity;
  selectedOpportunityId?: string;
  handleSelectOpportunityChange: (id: string) => void;
  conversions: Record<string, SalesOpportunityConversion> | undefined;
  enableConversionLoading: boolean;
}

const SalesOpportunityGridItem = ({
  haveMoreThanOneResult,
  testId,
  isSelected,
  opportunity,
  selectedOpportunityId,
  handleSelectOpportunityChange,
  conversions,
  enableConversionLoading,
}: SalesOpportunityGridItemProps) => {
  const isNotTheSelectedOpportunity =
    selectedOpportunityId && selectedOpportunityId !== opportunity.id;

  const handleClickCard = useCallback(() => {
    handleSelectOpportunityChange(opportunity.id);
  }, [handleSelectOpportunityChange, opportunity.id]);

  return (
    <Grid
      size={haveMoreThanOneResult ? 6 : 12}
      sx={{
        opacity: isNotTheSelectedOpportunity ? 0.35 : 1,
      }}
    >
      <SalesOpportunityCard
        salesOpportunity={opportunity}
        reducedSize={haveMoreThanOneResult}
        isSelected={isSelected}
        conversionValues={conversions?.[opportunity.id]}
        testId={testId}
        loadingConversion={enableConversionLoading && !conversions?.[opportunity.id]}
        handleClickCard={handleClickCard}
      />
    </Grid>
  );
};

const CustomerSalesOpportunities = ({ order }: ICustomerSalesOpportunitiesProps) => {
  const { classes } = useOrderBookStyles();
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | undefined>();
  const { isEnableOrderBookSalesOpportunityConversions } = useFeatureFlag();

  const handleSelectOpportunityChange = useCallback(
    (id: string) => {
      if (id === selectedOpportunityId) {
        setSelectedOpportunityId(undefined);
        return;
      }

      if (!selectedOpportunityId) {
        setSelectedOpportunityId(id);
      }
    },
    [selectedOpportunityId],
  );

  const { data, isFetching } = useGetSalesOpportunities({
    customerId: order.customer?.id!,
  });

  const {
    data: conversions,
    isFetching: isFetchingConversions,
    refetch,
  } = useSalesOpportunityConversion({
    enabled: !!data.list.length && isEnableOrderBookSalesOpportunityConversions,
    idsToFetchConversion: data.list.map((opportunity) => opportunity.id),
    customerId: order.customer?.id!,
  });

  useEffect(() => {
    if (data.list.length && isEnableOrderBookSalesOpportunityConversions) {
      refetch();
    }
  }, [data.list, isEnableOrderBookSalesOpportunityConversions, refetch]);

  useEffect(() => {
    const orderListElement = document.getElementById(OrderbookIds.ORDERBOOK_LIST);
    const orderFootertElement = document.getElementById(OrderbookIds.ORDERBOOK_FOOTER);

    if (selectedOpportunityId) {
      orderListElement?.classList.add(classes.disabled);
      orderFootertElement?.classList.add(classes.disabled);
      return;
    }

    if (!selectedOpportunityId) {
      orderListElement?.classList.remove(classes.disabled);
      orderFootertElement?.classList.remove(classes.disabled);
    }
  }, [classes.disabled, selectedOpportunityId]);

  const haveMoreThanOneResult = data.count > 1;

  const enableConversionLoading =
    isFetchingConversions && isEnableOrderBookSalesOpportunityConversions;

  if (!data.list.length && !isFetching) {
    return null;
  }

  return (
    <Grid size={12}>
      <Grid container spacing={1}>
        {data.list?.map((opportunity, index) => {
          const listLengthIsOdd = data.list.length % 2 !== 0;
          const isLastItem = index === data.list.length - 1;
          const shouldRenderLastItem = isLastItem && listLengthIsOdd;

          if (!(index % 2 === 0 || shouldRenderLastItem)) {
            return null;
          }

          const nextOpportunity = isLastItem ? null : data.list[index + 1];

          return (
            <Grid key={opportunity.id} size={12}>
              <Grid container>
                <Grid size={12}>
                  <Grid container spacing={1} className={classes.horizontalPadding}>
                    <SalesOpportunityGridItem
                      conversions={conversions}
                      enableConversionLoading={
                        enableConversionLoading && !conversions?.[opportunity.id]
                      }
                      handleSelectOpportunityChange={handleSelectOpportunityChange}
                      haveMoreThanOneResult={haveMoreThanOneResult}
                      isSelected={selectedOpportunityId === opportunity.id}
                      opportunity={opportunity}
                      selectedOpportunityId={selectedOpportunityId}
                      testId={`sales-opportunity-card-${index}`}
                    />
                    {nextOpportunity && (
                      <SalesOpportunityGridItem
                        conversions={conversions}
                        enableConversionLoading={
                          enableConversionLoading && !conversions?.[nextOpportunity.id]
                        }
                        handleSelectOpportunityChange={handleSelectOpportunityChange}
                        haveMoreThanOneResult={haveMoreThanOneResult}
                        isSelected={selectedOpportunityId === nextOpportunity.id}
                        opportunity={nextOpportunity}
                        selectedOpportunityId={selectedOpportunityId}
                        testId={`sales-opportunity-card-${index + 1}`}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <SalesOpportunityDetails
                    open={selectedOpportunityId === opportunity.id}
                    salesOpportunity={opportunity}
                    testId={`sales-opportunity-details-${index}`}
                  />
                </Grid>
                {nextOpportunity && (
                  <Grid size={12}>
                    <SalesOpportunityDetails
                      open={selectedOpportunityId === nextOpportunity.id}
                      salesOpportunity={nextOpportunity}
                      testId={`sales-opportunity-details-${index + 1}`}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          );
        })}
        {isFetching && (
          <Grid size={12}>
            <Box className={classes.horizontalPadding}>
              <LinearProgress />
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default CustomerSalesOpportunities;
